'use client';

import { motion } from 'framer-motion';
import { Clock, Trophy } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { courseModules, TOTAL_DURATION_MINUTES, TOTAL_LESSONS, TOTAL_MODULES } from '@/lib/course-data';
import { formatDuration } from '@/lib/utils';

export function ModulesPreview() {
  return (
    <section id="programa" className="container-page py-20 sm:py-24">
      <div className="mx-auto max-w-3xl text-center">
        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          className="heading-display text-3xl text-cream sm:text-4xl lg:text-5xl"
        >
          El programa{' '}
          <span className="text-gold-gradient">completo</span>
        </motion.h2>
        <p className="mt-4 text-lg text-cream/65">
          {TOTAL_LESSONS} lecciones agrupadas en {TOTAL_MODULES} módulos. Total{' '}
          {formatDuration(TOTAL_DURATION_MINUTES)} de contenido riguroso. Quizzes
          al final de cada módulo.
        </p>
      </div>

      <div className="mt-12 grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <Accordion type="single" collapsible className="space-y-3">
            {courseModules.map((mod) => (
              <AccordionItem
                key={mod.slug}
                value={mod.slug}
                className="border-white/10"
              >
                <AccordionTrigger className="px-5 py-4">
                  <div className="flex flex-1 items-center gap-4 text-left">
                    <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border border-gold-500/30 bg-gold-500/5 font-display text-2xl font-bold text-gold-300">
                      {mod.number}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-display text-lg font-semibold text-cream sm:text-xl">
                        {mod.title}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-cream/55">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />{' '}
                          {formatDuration(mod.durationMinutes)}
                        </span>
                        <span>·</span>
                        <span>{mod.lessons.length} lecciones</span>
                        <span>·</span>
                        <span className="flex items-center gap-1 text-gold-300">
                          <Trophy className="h-3.5 w-3.5" /> Badge "{mod.badge}"
                        </span>
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-5">
                  <p className="mb-4 text-sm leading-relaxed text-cream/70">
                    {mod.description}
                  </p>
                  <ol className="space-y-1.5 text-sm text-cream/80">
                    {mod.lessons.map((l) => (
                      <li
                        key={l.slug}
                        className="flex items-baseline justify-between gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-white/5"
                      >
                        <span className="flex items-baseline gap-3">
                          <span className="font-mono text-xs text-gold-400">
                            {l.number}
                          </span>
                          <span>{l.title}</span>
                        </span>
                        <span className="font-mono text-xs text-cream/45">
                          {l.durationMinutes} min
                        </span>
                      </li>
                    ))}
                  </ol>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <aside className="space-y-4 lg:col-span-4">
          <div className="card-premium space-y-4 p-6">
            <h3 className="font-display text-xl font-semibold text-gold-300">
              Lo que conseguirás
            </h3>
            <ul className="space-y-3 text-sm text-cream/75">
              <li className="flex gap-2">
                <span className="text-gold-400">✓</span>
                Marco mental claro para invertir en oro físico.
              </li>
              <li className="flex gap-2">
                <span className="text-gold-400">✓</span>
                Conocimiento técnico de formatos, primas y certificación.
              </li>
              <li className="flex gap-2">
                <span className="text-gold-400">✓</span>
                Comprensión completa de la fiscalidad española del oro.
              </li>
              <li className="flex gap-2">
                <span className="text-gold-400">✓</span>
                Estrategia de cartera con asignación basada en evidencia.
              </li>
              <li className="flex gap-2">
                <span className="text-gold-400">✓</span>
                Certificado de Oro Express, verificable en PDF.
              </li>
            </ul>
          </div>
          <div className="card-premium space-y-3 p-6">
            <div className="text-xs uppercase tracking-[0.18em] text-gold-300">
              Inversión real
            </div>
            <div className="font-display text-3xl font-bold text-cream">0€</div>
            <p className="text-sm text-cream/60">
              Sin tarjeta. Sin compromiso. Sin venta encubierta. Es nuestra
              forma de demostrar el rigor con el que trabaja Oro Express.
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}
